import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import Qr from './pages/Qr';
import EmptyPage from './pages/EmptyPage';
import reportWebVitals from './reportWebVitals';
import { SocketProvider } from './context/SocketContext';
import { apiUrl } from './config';

import { installViewportVars } from './utils/viewport';
installViewportVars();

// localStorage keys cleared when the user enters a NEW session (different uuid).
const SESSION_KEYS = [
  'museumx_nickname',
  'museumx_db_user_id',
  'museumx_created_at',
  'museumx_profile_index',
  'museumx_profile_image',
];

function wipeForNewSession() {
  SESSION_KEYS.forEach((k) => { try { localStorage.removeItem(k); } catch {} });
  try { sessionStorage.removeItem('museumx_started'); } catch {}
}

/**
 * QR gate — the user app only mounts when the visitor arrived via a valid
 * QR scan. The QR encodes `<PUBLIC_BASE_URL>/?session=<uuid>`; the token is
 * validated against the live active session.
 *   - no token / stale token / ended session -> EmptyPage (locked screen)
 *   - token matches the active session        -> mount the app
 * The token is cached in sessionStorage so an in-session refresh survives
 * (Chrome can drop the query string on navigation).
 */
function Gate() {
  const [state, setState] = useState('checking'); // checking | ok | blocked

  useEffect(() => {
    const urlToken = new URLSearchParams(window.location.search).get('session');
    let storedToken = null;
    try { storedToken = sessionStorage.getItem('museumx_session_token'); } catch {}
    const token = urlToken || storedToken;

    if (urlToken) {
      try { sessionStorage.setItem('museumx_session_token', urlToken); } catch {}
    }

    if (!token) { setState('blocked'); return; }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl('/api/activeSession'), { cache: 'no-store' });
        const json = await res.json();
        const activeUuid = json?.sessionUuid || null;
        const status = (json?.status || '').toString().toUpperCase();
        const ok = !!activeUuid && status !== 'ENDED' && activeUuid === token;

        if (ok) {
          // Entering a different session than last time -> drop stale registration.
          let last = null;
          try { last = localStorage.getItem('museumx_last_session_uuid'); } catch {}
          if (last && last !== activeUuid) wipeForNewSession();
          try { localStorage.setItem('museumx_last_session_uuid', activeUuid); } catch {}
        } else {
          try { sessionStorage.removeItem('museumx_session_token'); } catch {}
        }

        if (!cancelled) setState(ok ? 'ok' : 'blocked');
      } catch {
        if (!cancelled) setState('blocked');
      }
    })();

    return () => { cancelled = true; };
  }, []);

  if (state !== 'ok') return <EmptyPage />;

  return (
    <SocketProvider>
      <App />
    </SocketProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));

// /qr -> operator QR display (no gate, no socket). Everything else -> the gated app.
if (window.location.pathname.replace(/\/+$/, '') === '/qr') {
  root.render(<Qr />);
} else {
  root.render(<Gate />);
}

reportWebVitals();
